'use client'

import { zodResolver } from '@hookform/resolvers/zod'
import { useRouter } from 'next/navigation'
import { useCallback, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useSearchParams } from 'next/navigation'
import { toast } from 'sonner'
import { IRegisterSchema, RegisterSchema } from 'schemas/register.schema'
import { AuthenticationService } from 'services/authentication.service'
import { Button } from 'ui/components/button'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'ui/components/form'
import { Input } from 'ui/components/input'
import { useError } from 'kach-components'
import Image from 'next/image'
import Link from 'next/link'
import { AiOutlineMail } from 'react-icons/ai'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from 'ui/components/card'
import { useMemo } from 'react'

export const RegisterSCN = () => {
  const router = useRouter()
  const searchParams = useSearchParams()

  // Obtener email de query params si existe
  const defaultEmail = useMemo(() => {
    const rawEmail = searchParams?.get('email') || ''
    return rawEmail.replace(/ /g, '+') // Corrige el espacio interpretado
  }, [searchParams])

  const form = useForm<IRegisterSchema>({
    resolver: zodResolver(RegisterSchema),
    defaultValues: {
      user: {
        email: defaultEmail,
        firstName: '',
        lastName: '',
        username: '',
        password: '',
      },
      company: {
        name: '',
        ein: '',
      },
    },
  })

  const [isLoading, setIsLoading] = useState(false)
  const [emailSent, setEmailSent] = useState(false)

  const { getErrorMessage } = useError()

  const watchedEmail = form.watch('user.email')

  const onSubmit = useCallback(
    (data: IRegisterSchema) => {
      setIsLoading(true)
      const toastId = toast.loading('Registrando...')

      AuthenticationService.register({
        ...data,
        strategy: 'local',
      })
        .then(() => {
          toast.success('Registro exitoso!', { id: toastId })
          setEmailSent(true)
        })
        .catch((err) => {
          toast.error(getErrorMessage(err), { id: toastId })
          setIsLoading(false)
        })
    },
    [getErrorMessage],
  )

  if (emailSent) {
    return (
      <div className='flex min-h-screen flex-col justify-center items-center py-6 px-4 bg-sidebar'>
        <Card className='w-full max-w-lg'>
          <CardContent className='pt-10 pb-10 flex flex-col items-center text-center'>
            <AiOutlineMail className='text-6xl text-muted-foreground mb-4' />
            <CardTitle className='text-2xl mb-2'>
              Verifica tu correo electrónico
            </CardTitle>
            <CardDescription className='text-base'>
              Para poder utilizar nuestros servicios, necesitamos que verifiques
              tu dirección de correo electrónico. Por eso, acabamos de enviarte
              un correo electrónico a <strong>{watchedEmail}</strong> con
              instrucciones sobre cómo verificar tu cuenta.
            </CardDescription>
            <Link
              href='/login'
              className='mt-6 text-sm text-primary hover:underline'
            >
              Volver al inicio de sesión
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className='flex min-h-screen flex-col justify-center py-6 px-0 md:px-6 lg:px-8 bg-sidebar'>
      <div className='w-full max-w-lg bg-white py-6 px-4 md:px-6 shadow-md rounded-lg mx-auto'>
        <Image
          src='/kach-192x192.png'
          alt='Kach'
          width={40}
          height={40}
          style={{ borderRadius: '25%' }}
        />
        <p className='font-semibold text-[2rem] mt-4 mb-2 text-foreground tracking-tighter'>
          Regístrate en Kach.
        </p>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className='flex flex-col gap-4'
            noValidate
          >
            {/* Sección: Información del usuario */}
            <div className='flex flex-col gap-4'>
              <h3>Información personal</h3>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name='user.firstName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Nombre</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Ingrese su nombre'
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='user.lastName'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Apellido</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Ingrese su apellido'
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name='user.email'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Email</FormLabel>
                    <FormControl>
                      <Input
                        type='email'
                        placeholder='Ingrese su email'
                        disabled={isLoading || !!defaultEmail}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='user.username'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Usuario</FormLabel>
                    <FormControl>
                      <Input
                        placeholder='Ingrese su usuario'
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name='user.password'
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Contraseña</FormLabel>
                    <FormControl>
                      <Input
                        type='password'
                        placeholder='Ingrese su contraseña'
                        disabled={isLoading}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Sección: Información del estudio */}
            <div className='flex flex-col gap-4'>
              <h3>Información del estudio de investigación</h3>

              <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
                <FormField
                  control={form.control}
                  name='company.name'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>Nombre del estudio</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Ingrese el nombre del estudio'
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name='company.ein'
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel required>CUIT</FormLabel>
                      <FormControl>
                        <Input
                          placeholder='Ingrese el CUIT del estudio'
                          disabled={isLoading}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            <Button
              type='submit'
              className='w-full'
              size='lg'
              disabled={isLoading}
              isLoading={isLoading}
              textCenter
            >
              <span className='text-base'>
                {isLoading ? 'Registrando...' : 'Registrarme'}
              </span>
            </Button>
          </form>
        </Form>

        <div className='mt-6 flex items-center justify-center gap-2'>
          <span className='text-sm text-muted-foreground'>
            ¿Ya tienes una cuenta?
          </span>
          <Link href='/login' className='text-sm text-primary hover:underline'>
            Inicia sesión
          </Link>
        </div>
      </div>
    </div>
  )
}
